import { isNil } from 'lodash'
import { useEffect, useRef } from 'react'

/**
 * Focuses the next focusable element in the DOM relative to the given element.
 * This function finds all focusable elements on the page and moves focus to the element
 * at the specified offset from the current element's position.
 *
 * @param element - The reference element to calculate the next focus target from
 * @param offset - The number of positions to move (default: 0 for same element, 1 for next, -1 for previous)
 */
function focusNextElement (element: HTMLElement | null, offset = 0): void {
  // Get all focusable elements in the document
  const focusable = Array.from(
    document.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => {
    const castedEl = el as HTMLElement
    // Only include elements that are visible (have an offsetParent)
    return castedEl.offsetParent !== null
  }) as HTMLElement[]

  // Find the index of the current element in the focusable elements array
  const index = focusable.indexOf(element as HTMLElement)
  if (index === -1) return // Element not found in focusable elements

  // Calculate the target element based on the offset
  const next = focusable[index + offset]
  // Focus the target element if it exists
  next?.focus()
}

/**
 * Custom React hook that detects when an element receives or loses focus via Tab key navigation.
 * This hook is useful for implementing custom tab behavior, accessibility features, or UI state changes
 * that should only occur when users navigate using the keyboard (Tab key) rather than mouse clicks.
 *
 * @param element - The HTML element to monitor for tab-based focus events
 * @param onFocusedByTab - Optional callback function called when the element receives focus via Tab key
 * @param onNotFocusedByTab - Optional callback function called when the element loses focus via Tab key
 */
export function useTabFocusDetection (
  element: HTMLElement | null,
  onFocusedByTab?: (element: HTMLElement | null) => void,
  onNotFocusedByTab?: (element: HTMLElement | null) => void
): void {
  // Track whether the last keydown event was a Tab key press
  const keyDownRef = useRef(false)

  /**
   * Handles keydown events to detect Tab key presses.
   * Sets a flag that will be used by focus/blur handlers to determine
   * if the focus change was caused by Tab navigation.
   */
  const handleKeyDown = (e: KeyboardEvent): void => {
    keyDownRef.current = e.key === 'Tab'
  }

  /**
   * Handles focus events on the monitored element.
   * If the focus was caused by a Tab key press, calls the onFocusedByTab callback.
   */
  const handleFocus = (e: FocusEvent): void => {
    e.stopPropagation()
    e.preventDefault()
    if (keyDownRef.current) {
      onFocusedByTab?.(element)
      keyDownRef.current = false // Reset the flag after handling
    }
  }

  /**
   * Handles blur events on the monitored element.
   * If the blur was caused by Tab navigation, calls the onNotFocusedByTab callback
   * and attempts to focus the next element in the tab order.
   */
  const handleBlur = (e: FocusEvent): void => {
    if (keyDownRef.current) {
      onNotFocusedByTab?.(element)
      keyDownRef.current = false // Reset the flag after handling
      // Attempt to focus the current element first (offset 0)
      focusNextElement(element)
      // Then try to focus the next element (offset 1) on the next tick
      // This ensures proper tab order navigation
      setTimeout(() => {
        focusNextElement(element, 1)
      }, 0)
    }
  }

  // Set up event listeners when the element changes
  useEffect(() => {
    if (isNil(element)) return // Don't attach listeners if element is null/undefined
    // Listen for keydown events globally to detect Tab key presses
    window.addEventListener('keydown', handleKeyDown)
    // Listen for focus and blur events on the specific element
    element.addEventListener('focus', handleFocus)
    element.addEventListener('blur', handleBlur)

    // Cleanup function to remove event listeners when component unmounts
    // or when the element changes
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      element.removeEventListener('focus', handleFocus)
      element.removeEventListener('blur', handleBlur)
    }
  }, [element]) // Re-run effect when element changes
}
